import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { Button } from "@dashboard/components/Button";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import { useBaseChannelsQuery } from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { Box, Text } from "@saleor/macaw-ui-next";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { GoogleExportModal } from "../../components/GoogleExportModal/GoogleExportModal";
import { messages } from "./messages";

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid #e5e7eb",
  fontSize: 14,
  backgroundColor: "#fff",
  width: "100%",
};

export const ExportOverview = () => {
  const intl = useIntl();
  const notify = useNotifier();

  const [channelSlug, setChannelSlug] = useState<string>("");
  const [storefrontUrl, setStorefrontUrl] = useState<string>("");
  const [pushOpen, setPushOpen] = useState(false);
  // The crawlable feed URL is returned by the push mutation, not queried up
  // front — it appears here after the first successful push.
  const [feedUrl, setFeedUrl] = useState<string>("");

  const { data: channelsData } = useBaseChannelsQuery();
  const channels = channelsData?.channels ?? [];

  // Default to the first channel once channels load.
  useEffect(() => {
    if (!channelSlug && channels.length > 0) {
      setChannelSlug(channels[0].slug);
    }
  }, [channels, channelSlug]);

  const handleCopy = () => {
    if (!feedUrl) return;

    navigator.clipboard.writeText(feedUrl);
    notify({ status: "success", title: intl.formatMessage(messages.copied) });
  };

  return (
    <>
      <ListPageLayout>
        <TopNav title={intl.formatMessage(messages.pageTitle)} withoutBorder />

        <Box display="flex" flexDirection="column" gap={4} padding={6}>
          {/* Channel + storefront configuration */}
          <DashboardCard>
            <DashboardCard.Header>
              <DashboardCard.Title>
                <FormattedMessage {...messages.channelTitle} />
              </DashboardCard.Title>
            </DashboardCard.Header>
            <DashboardCard.Content>
              <Box display="flex" flexDirection="column" gap={4}>
                <Text size={2} color="default2">
                  <FormattedMessage {...messages.channelDescription} />
                </Text>
                <select
                  style={{ ...inputStyle, cursor: "pointer", maxWidth: 360 }}
                  value={channelSlug}
                  onChange={e => setChannelSlug(e.target.value)}
                >
                  {channels.map(channel => (
                    <option key={channel.id} value={channel.slug}>
                      {channel.name}
                    </option>
                  ))}
                </select>

                <Box display="flex" flexDirection="column" gap={2} style={{ maxWidth: 360 }}>
                  <Text size={3} fontWeight="medium">
                    <FormattedMessage {...messages.storefrontUrlLabel} />
                  </Text>
                  <input
                    style={inputStyle}
                    value={storefrontUrl}
                    onChange={e => setStorefrontUrl(e.target.value)}
                    placeholder="https://shop.example.com"
                  />
                  <Text size={1} color="default2">
                    <FormattedMessage {...messages.storefrontUrlHelp} />
                  </Text>
                </Box>
              </Box>
            </DashboardCard.Content>
          </DashboardCard>

          {/* Crawlable feed URL */}
          <DashboardCard>
            <DashboardCard.Header>
              <DashboardCard.Title>
                <FormattedMessage {...messages.feedUrlTitle} />
              </DashboardCard.Title>
            </DashboardCard.Header>
            <DashboardCard.Content>
              <Box display="flex" flexDirection="column" gap={4}>
                <Text size={2} color="default2">
                  <FormattedMessage {...messages.feedUrlDescription} />
                </Text>
                <Box
                  padding={3}
                  borderRadius={3}
                  backgroundColor="default2"
                  style={{ wordBreak: "break-all", fontFamily: "monospace" }}
                >
                  <Text size={2} color={feedUrl ? "default1" : "default2"}>
                    {feedUrl || intl.formatMessage(messages.feedUrlPending)}
                  </Text>
                </Box>
                <Box display="flex" gap={3}>
                  <Button variant="secondary" onClick={handleCopy} disabled={!feedUrl}>
                    <FormattedMessage {...messages.copyUrl} />
                  </Button>
                  {feedUrl && (
                    <a href={feedUrl} target="_blank" rel="noreferrer">
                      <Button variant="tertiary">
                        <FormattedMessage {...messages.openUrl} />
                      </Button>
                    </a>
                  )}
                </Box>
              </Box>
            </DashboardCard.Content>
          </DashboardCard>

          {/* Push via Content API */}
          <DashboardCard>
            <DashboardCard.Header>
              <DashboardCard.Title>
                <FormattedMessage {...messages.pushTitle} />
              </DashboardCard.Title>
            </DashboardCard.Header>
            <DashboardCard.Content>
              <Box display="flex" flexDirection="column" gap={4} alignItems="flex-start">
                <Text size={2} color="default2">
                  <FormattedMessage {...messages.pushDescription} />
                </Text>
                <Button variant="primary" onClick={() => setPushOpen(true)} disabled={!channelSlug}>
                  <FormattedMessage {...messages.pushButton} />
                </Button>
              </Box>
            </DashboardCard.Content>
          </DashboardCard>
        </Box>
      </ListPageLayout>

      <GoogleExportModal
        open={pushOpen}
        channelSlug={channelSlug}
        storefrontUrl={storefrontUrl}
        onClose={() => setPushOpen(false)}
        onPushed={url => setFeedUrl(url ?? "")}
      />
    </>
  );
};

export default ExportOverview;
